import {
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import DatesPickerDropdown from 'src/components/DatesPickerDropdown/DatesPickerDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import Spotlight from 'src/components/Spotlight/Spotlight'
import SvgSettings from 'src/components/Svgs/SvgSettings'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { TransactionsFilters } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import {
  useBackgroundDispatch,
  useChangedEffect,
  useCookieState,
  usePrevious,
} from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  getOrgExpenseReportStatus,
  OrgExpenseReportConfirmDateChangePopup,
  OrgExpenseReportStatus,
  orgExpenseReportStatusToColor,
  orgExpenseReportStatusToString,
} from 'src/models/orgExpenseReports.models'
import {
  TransactionExpenseCategoryStatus,
  TransactionReceiptStatus,
} from 'src/models/transaction.models'
import { expenseSettingsUrl } from 'src/navigation/urls'
import {
  usePatchOrgExpenseReport,
  useQueryOrgExpenseReport,
  useQueryOrgExpenseReportCycle,
  useQueryOrgExpenseReportCycleReports,
  useQueryOrgExpenseReportMetrics,
} from 'src/queries/orgExpenseReports.queries'
import {
  ORG_EXPENSE_REPORTS_CYCLES_QK,
  ORG_EXPENSE_REPORTS_QK,
} from 'src/queries/queries.constants'
import { createSetConfirmOrgExpenseReportDateChangePopup } from 'src/redux/actions/app.actions'
import { getReceiptRules } from 'src/redux/actions/data/organizations.actions'
import { createSetOrgExpenseReportDateUpdateConfirmed } from 'src/redux/actions/data/orgExpenseReports.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectDateUpdateRequiresConfirmation,
  selectExpenseReportIdFromRoute,
} from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

interface MetricBoxProps {
  title: string
  titleTooltip?: React.ReactElement
  creditCard?: CreditCardListModel
  onClick?: () => void
  text: React.ReactElement
  tag?: React.ReactElement
  loading: boolean
  linkTestId: string
  boxTestId: string
}

const MetricBox: React.FC<MetricBoxProps> = (props): React.ReactElement => {
  return (
    <Box
      noPadding
      testId={props.boxTestId}
      css={css`
        flex: 1 1 0;
        width: 0px;
        display: flex;
        flex-basis: 25%;
        max-width: 220px;
        flex-grow: 1;
        flex-direction: column;
        justify-content: space-between;
        padding: 12px;
        gap: 8px;
        height: 100%;
        position: relative;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 4px;
        `}
      >
        <Trns source={props.title} />
        <span>{props.titleTooltip}</span>
      </span>
      {props.loading ? (
        <span
          css={css`
            flex-basis: 60%;
            padding-bottom: 30px;
          `}
        >
          <LoadingShimmer width={'60px'} />
        </span>
      ) : props.onClick !== undefined ? (
        <HoverableLink
          css={css`
            flex-basis: 60%;
            padding-bottom: 30px;
          `}
          onClick={props.onClick}
          ariaLabelSource={props.title}
          hoverUnderline
          regular
          size={20}
          testId={props.linkTestId}
        >
          {props.text}
        </HoverableLink>
      ) : (
        <span
          css={css`
            flex-basis: 60%;
            padding-bottom: 30px;
            ${getFontCss(
              20,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.regular,
            )};
          `}
          data-testid={props.linkTestId}
        >
          {props.text}
        </span>
      )}
    </Box>
  )
}

interface OrgExpenseReportSummaryBarProps {
  setInitialLoad: (initialLoad: boolean) => void
  creditCard?: CreditCardListModel
  setExternallyAppliedFilters?: (filters: TransactionsFilters) => void
}

const OrgExpenseReportSummaryBar: React.FC<OrgExpenseReportSummaryBarProps> = (
  props,
): React.ReactElement => {
  const reportId = useSelector(selectExpenseReportIdFromRoute)
  const { data: report } = useQueryOrgExpenseReport({ reportId })
  const { data: cycle } = useQueryOrgExpenseReportCycle({
    cycleId: report?.cycleId,
  })
  const { data: previousReport } = useQueryOrgExpenseReport({
    reportId: cycle?.previousReportId,
  })
  const { data: metrics, isPending: metricsPending } =
    useQueryOrgExpenseReportMetrics({ reportId })
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const {
    mutate: patchOrgExpenseReport,
    reset,
    error,
    isPending,
  } = usePatchOrgExpenseReport()
  const isReceiptRulesOn = organization?.receiptRule?.enabled
  const dateChangeConfirmed = useSelector(selectDateUpdateRequiresConfirmation)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const { data: cycleHistoricalReports } = useQueryOrgExpenseReportCycleReports(
    { cycleId: report?.cycleId },
  )

  // TODO - only use metrics?.physicalCardTotalSpentCents check until BE updates Regions cards to accurately set physicalClearingsEnabled
  const showPhysicalCardSpend =
    organization?.physicalClearingsEnabled ||
    props.creditCard?.physicalClearingsEnabled ||
    metrics?.physicalCardTotalSpentCents !== 0

  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const queryClient = useQueryClient()

  const cyclePickerDisabled = useMemo(() => {
    return (
      reportId !== cycle?.previousReportId &&
      reportId !== cycle?.currentReportId
    )
  }, [report, cycle, reportId])
  const reportPrevious = usePrevious(report)

  const [updatedUntilForPatch, setUpdatedUntilForPatch] = useState<
    string | undefined
  >()

  useEffect(() => {
    if (organization?.id !== undefined && isReceiptRulesOn === undefined) {
      dispatch(getReceiptRules(organization.id))
    }
  }, [organization?.id])

  // re-fetch cycle and previous report details if report end date changes
  useChangedEffect(() => {
    if (reportPrevious && reportPrevious?.until !== report?.until) {
      if (report) {
        queryClient.invalidateQueries({
          queryKey: [
            ORG_EXPENSE_REPORTS_QK,
            ORG_EXPENSE_REPORTS_CYCLES_QK,
            report.cycleId,
          ],
        })
      }
    }
  }, [reportPrevious, report])

  // send patch request if date change is confirmed
  useChangedEffect(() => {
    if (dateChangeConfirmed && updatedUntilForPatch && report) {
      patchOrgExpenseReport({
        id: reportId,
        body: { until: updatedUntilForPatch },
      })
      dispatch(createSetOrgExpenseReportDateUpdateConfirmed(false))
      setUpdatedUntilForPatch(undefined)
      analytics?.track('rcn report cycle updated', {
        card_holder: report.accountUserId,
        cycle_type: cycle?.cycleType,
        original_cycle_date: report.until,
        updated_cycle_date: moment.utc(updatedUntilForPatch).format(),
      })
    }
  }, [dateChangeConfirmed, updatedUntilForPatch, report])

  const {
    cookieValue: checkedSecondReportCycleDate,
    updateCookieValue: setCheckedSecondReportCycleDate,
  } = useCookieState<string[]>(
    CookieNames.Expenses,
    CookieValuePaths.checkedSecondReportCycleDate,
    [],
  )

  const showCheckReportDateReminder =
    cycle !== undefined &&
    // has not been dismissed previously
    !checkedSecondReportCycleDate.find((id) => id === cycle.id) &&
    // is the second and current report
    cycleHistoricalReports?.length === 2 &&
    cycle.currentReportId === reportId

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 18px;
        padding: 24px 40px;
      `}
    >
      {report === undefined ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              max-width: 45%;
            `}
          >
            <UserAvatarAndName
              textCss={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
              user={{
                id: report.accountUserId,
                fullNameOrEmail:
                  report.accountUserFullName || report.accountUserEmail,
                initials:
                  `${report.accountUserFirstName?.slice(0, 1)}${report.accountUserLastName?.slice(0, 1)}` ||
                  report.accountUserEmail[0],
                avatarUrl: report.accountUserAvatarUrl,
                hasFullName: true,
              }}
            />
            <span
              css={css`
                ${getFontCss(
                  28,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-top: 12px;
                margin-bottom: 8px;
                & > span {
                  padding-inline-end: 12px;
                }
              `}
            >
              <span data-testid={'accountName'}>{report?.accountName}</span>
              <span data-testid={'accountLast4Tag'}>
                <Tag
                  text={report.accountLast4.toString()}
                  size={TagSizes.Medium}
                />
              </span>
              {metrics === undefined ? (
                <span
                  css={css`
                    display: inline-flex;
                    line-height: 32px;
                    vertical-align: middle;
                  `}
                >
                  <LoadingShimmer width={'60px'} height={'24px'} />
                </span>
              ) : (
                <span data-testid={'reportStatusTag'}>
                  <Tag
                    text={
                      <Trns
                        source={orgExpenseReportStatusToString(
                          getOrgExpenseReportStatus(report, metrics),
                        )}
                      />
                    }
                    color={orgExpenseReportStatusToColor(
                      getOrgExpenseReportStatus(report, metrics),
                    )}
                    size={TagSizes.Medium}
                  />
                </span>
              )}
            </span>
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <span
                css={css`
                  margin-right: 4px;
                `}
              >
                <Trns
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                  source={'Report Date'}
                />
                {':'}
              </span>
              <DatesPickerDropdown
                since={moment.utc(report.since)}
                until={moment.utc(report.until)}
                minimumRangeSize={0}
                sinceLabel={<Trns source={'Start Date'} />}
                untilLabel={<Trns source={'Close Date'} />}
                sinceTooltipText={
                  <Trns
                    source={
                      'The report start date of a report is determined by the end date of the previous report. To update the start date, please update the end date of the previous report.'
                    }
                  />
                }
                disabled={cyclePickerDisabled ? true : 'since'}
                loading={isPending}
                initialMonth={moment.utc(report.until)}
                isOutsideRange={(day): boolean => {
                  const isAfterRange = day
                    .utc()
                    .isAfter(moment.utc().add(3, 'month'), 'day')
                  if (
                    reportId === cycle?.currentReportId ||
                    reportId === cycle?.previousReportId
                  ) {
                    return (
                      isAfterRange ||
                      day.utc().isBefore(moment.utc(report?.since), 'day')
                    )
                  } else {
                    return true
                  }
                }}
                setSince={(): void => {
                  return
                }}
                setUntil={(): void => {
                  return
                }}
                onProcessDatesChange={(
                  since,
                  until,
                ): {
                  since: moment.Moment | undefined
                  until: moment.Moment | undefined
                } => {
                  let processedSince = since?.utc()
                  let processedUntil = until?.utc()
                  // if until is null, user selected a date before since
                  if (!until) {
                    processedSince = previousReport
                      ? moment.utc(previousReport.since)
                      : since
                    processedUntil = since
                  } else if (until && report && until.isAfter(report.since)) {
                    processedSince = moment.utc(report.since)
                    processedUntil = until
                  }
                  return { since: processedSince, until: processedUntil }
                }}
                onApplyDates={(_, until): void => {
                  setUpdatedUntilForPatch(until?.utc().format('YYYY-MM-DD'))
                  if (
                    moment.utc(report.until).isBefore(moment.utc(), 'day') &&
                    until?.isAfter(moment.utc(), 'day')
                  ) {
                    // past report, being updated to future date
                    dispatch(
                      createSetConfirmOrgExpenseReportDateChangePopup({
                        type: OrgExpenseReportConfirmDateChangePopup.MergeReports,
                        date: until,
                        rcnName: report.accountName,
                      }),
                    )
                  } else if (
                    moment
                      .utc(report.until)
                      .isSameOrAfter(moment.utc(), 'day') &&
                    until?.isBefore(moment.utc(), 'day')
                  ) {
                    // current report, being updated to past date
                    dispatch(
                      createSetConfirmOrgExpenseReportDateChangePopup({
                        type: OrgExpenseReportConfirmDateChangePopup.CreateNewReport,
                        date: until,
                        rcnName: report.accountName,
                      }),
                    )
                  } else {
                    // regular update, does not require confirmation modal
                    dispatch(createSetOrgExpenseReportDateUpdateConfirmed(true))
                  }
                }}
                onDropdownOpen={(): void => {
                  setCheckedSecondReportCycleDate([
                    ...checkedSecondReportCycleDate,
                    cycle?.id || '',
                  ])
                }}
                onDropdownClose={(): void => {
                  setUpdatedUntilForPatch(undefined)
                  reset()
                }}
                errorMessage={error?.error}
                footerContent={
                  <HoverableLink
                    to={
                      organization?.id
                        ? expenseSettingsUrl(organization?.id)
                        : location.pathname
                    }
                    ariaLabelSource={''}
                    regular
                    size={14}
                    css={css`
                      display: flex;
                      gap: 8px;
                      align-items: center;
                    `}
                  >
                    <SvgSettings />
                    <Trns source={'Statement Cycle Settings'} />
                  </HoverableLink>
                }
                utc={true}
                keepDropdownOpen={
                  isPending || !!error?.error || !!updatedUntilForPatch
                }
                buttonMode
              />
            </div>
            {showCheckReportDateReminder && (
              <Spotlight
                tooltip
                subtitle={
                  <Trns
                    source={
                      'Make sure your report dates align with your credit card statement. You can update these dates at any time.'
                    }
                  />
                }
                cookieFunction={(): void =>
                  setCheckedSecondReportCycleDate([
                    ...checkedSecondReportCycleDate,
                    cycle?.id || '',
                  ])
                }
                css={css`
                  position: absolute;
                  z-index: 3;
                  width: 320px;
                  margin-top: 116px;
                  margin-left: 80px;
                `}
              />
            )}
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              flex-grow: 1;
              justify-content: flex-end;
              gap: 8px;
            `}
          >
            <MetricBox
              title={'Total Card Spend'}
              boxTestId={'TotalSpend'}
              linkTestId={'totalSpendAmount'}
              loading={metricsPending}
              titleTooltip={
                <Tooltip
                  openLeft={false}
                  width={260}
                  onHover={(): void => {
                    analytics?.track('expense report spend hover')
                  }}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 16px;
                    `}
                  >
                    <div>
                      <Trns
                        source={
                          showPhysicalCardSpend
                            ? "${totalCardSpend} is the sum of cleared transactions from the physical card, virtual cards, and bill pay cards in this report. Here's the breakdown:"
                            : "${totalCardSpend} is the sum of cleared transactions from virtual cards and bill pay cards in this report. Here's the breakdown:"
                        }
                        expressions={{
                          totalCardSpend: (
                            <span
                              css={css`
                                font-family: ${styleConstants.fontFamilies
                                  .medium};
                              `}
                            >
                              <Trns source={'Total Card Spend'} />
                            </span>
                          ),
                        }}
                      />
                    </div>
                    <div>
                      {showPhysicalCardSpend && (
                        <p>
                          <span
                            css={css`
                              font-family: ${styleConstants.fontFamilies
                                .medium};
                              margin-right: 4px;
                            `}
                          >
                            <Trns source={'Physical Card:'} />
                          </span>
                          <Crncy
                            currency={report.accountCurrency}
                            amount={toDollars(
                              metrics?.physicalCardTotalSpentCents || 0,
                            )}
                            hideCurrency
                          />
                        </p>
                      )}
                      <p>
                        <span
                          css={css`
                            font-family: ${styleConstants.fontFamilies.medium};
                            margin-right: 4px;
                          `}
                        >
                          <Trns source={'Virtual Cards:'} />
                        </span>
                        <Crncy
                          currency={report.accountCurrency}
                          amount={toDollars(
                            metrics?.virtualCardSpentCents || 0,
                          )}
                          hideCurrency
                        />
                      </p>
                      <p>
                        <span
                          css={css`
                            font-family: ${styleConstants.fontFamilies.medium};
                            margin-right: 4px;
                          `}
                        >
                          <Trns source={'Bill Pay Cards:'} />
                        </span>
                        <Crncy
                          currency={report.accountCurrency}
                          amount={toDollars(metrics?.billPaySpentCents || 0)}
                          hideCurrency
                        />
                      </p>
                    </div>
                  </div>
                </Tooltip>
              }
              text={
                <Crncy
                  currency={report.accountCurrency}
                  amount={toDollars(metrics?.totalSpentCents || 0)}
                  hideCurrency
                />
              }
            />
            <MetricBox
              title={'Transactions Pending Review'}
              boxTestId={'TransactionsPendingReview'}
              linkTestId={'TransactionsPendingReviewCount'}
              loading={metricsPending}
              onClick={(): void => {
                props.setExternallyAppliedFilters?.({
                  reviewStatus: new Set<string>([OrgExpenseReportStatus.PendingReview]),
                })
              }}
              text={
                <>
                  {metrics === undefined
                    ? '0/0'
                    : metrics.transactionsTotalCount -
                      metrics.transactionsReviewedCount +
                      '/' +
                      metrics.transactionsTotalCount}
                </>
              }
            />
            <MetricBox
              title={'Missing Receipts'}
              boxTestId={'MissingReceipts'}
              linkTestId={'MissingReceiptsCount'}
              loading={metricsPending}
              onClick={(): void => {
                props.setExternallyAppliedFilters?.({
                  receiptStatus: new Set<string>([TransactionReceiptStatus.Missing]),
                  receiptRequired: true,
                })
              }}
              text={<>{metrics?.missingReceiptsCount}</>}
            />
            {activeIntegration !== undefined && (
              <MetricBox
                title={'Missing Categories'}
                boxTestId={'MissingCategories'}
                linkTestId={'MissingCategoriesCount'}
                loading={metricsPending}
                onClick={(): void => {
                  props.setExternallyAppliedFilters?.({
                    expenseCategoryStatuses: new Set<string>([TransactionExpenseCategoryStatus.Missing]),
                  })
                }}
                text={<>{metrics?.missingCategoriesCount}</>}
              />
            )}
          </div>
        </>
      )}
    </Box>
  )
}

export default OrgExpenseReportSummaryBar
